<script>
  import { onMount, tick } from 'svelte'
  import NeuronList from './NeuronList.svelte'
  import EditableTitle from './EditableTitle.svelte'
  import NeuronEditor from './NeuronEditor.svelte'
  import SearchPalette from './SearchPalette.svelte'
  import { csrfToken } from '../lib/csrf.js'
  import { isNativeApp, notifyNative, nativeNavigate, nativeNavigateHome } from '../lib/native.js'

  let { email = '' } = $props()

  let native = $state(isNativeApp())

  let currentNeuronId = $state(null)
  let currentNeuronTitle = $state(null)
  let currentNeuronBody = $state(null)
  let ancestors = $state([])
  let initialized = $state(false)
  let navigating = $state(false)
  let navEl = $state(null)
  let hiddenCount = $state(0)

  let displayItems = $derived.by(() => {
    if (hiddenCount === 0 || ancestors.length <= 2) {
      return ancestors.map(a => ({ type: 'ancestor', id: a.id, title: a.title }))
    }

    const items = [{ type: 'ancestor', id: ancestors[0].id, title: ancestors[0].title }]
    items.push({ type: 'ellipsis' })

    for (let i = 1 + hiddenCount; i < ancestors.length; i++) {
      items.push({ type: 'ancestor', id: ancestors[i].id, title: ancestors[i].title })
    }

    return items
  })

  function resetCollapse() {
    hiddenCount = 0
    checkOverflow()
  }

  async function checkOverflow() {
    await tick()
    if (!navEl || ancestors.length <= 2) return
    if (navEl.scrollWidth > navEl.clientWidth && hiddenCount < ancestors.length - 1) {
      hiddenCount++
      checkOverflow()
    }
  }

  function parseNeuronIdFromPath(path) {
    const match = path.match(/^\/neurons\/(\d+)/)
    return match ? parseInt(match[1]) : null
  }

  async function fetchNeuron(id) {
    const response = await fetch(`/api/neurons/${id}`)
    if (response.ok) {
      return await response.json()
    }
    return null
  }

  async function loadNeuronFromUrl() {
    const id = parseNeuronIdFromPath(window.location.pathname)
    if (id) {
      const neuron = await fetchNeuron(id)
      if (neuron) {
        currentNeuronId = neuron.id
        currentNeuronTitle = neuron.title
        currentNeuronBody = neuron.body
        ancestors = neuron.ancestors || []
        initialized = true
        resetCollapse()
        return
      }
    }
    currentNeuronId = null
    currentNeuronTitle = null
    currentNeuronBody = null
    ancestors = []
    initialized = true
  }

  async function navigateTo(neuron) {
    if (navigating) return
    navigating = true
    try {
      const full = await fetchNeuron(neuron.id)
      if (full) {
        if (nativeNavigate(full.id, full.title)) return
        currentNeuronId = full.id
        currentNeuronTitle = full.title
        currentNeuronBody = full.body
        ancestors = full.ancestors || []
        history.pushState(
          { neuronId: full.id, neuronTitle: full.title, ancestors: full.ancestors },
          '',
          `/neurons/${full.id}`
        )
        resetCollapse()
      }
    } finally {
      navigating = false
    }
  }

  let searchOpen = $state(false)

  let titleSaved = $state(false)
  let titleSavedTimer = null

  function showTitleSaved() {
    clearTimeout(titleSavedTimer)
    titleSaved = true
    titleSavedTimer = setTimeout(() => { titleSaved = false }, 1000)
  }

  async function saveCurrentNeuronTitle(newTitle) {
    const response = await fetch(`/api/neurons/${currentNeuronId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({ title: newTitle }),
    })
    if (response.ok) {
      const updated = await response.json()
      currentNeuronTitle = updated.title
    }
  }

  async function saveCurrentNeuron(newTitle, newBody) {
    const response = await fetch(`/api/neurons/${currentNeuronId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({ title: newTitle, body: newBody }),
    })
    if (response.ok) {
      const updated = await response.json()
      currentNeuronTitle = updated.title
      currentNeuronBody = updated.body
    }
  }

  function navigateHome() {
    if (nativeNavigateHome()) return
    currentNeuronId = null
    currentNeuronTitle = null
    currentNeuronBody = null
    ancestors = []
    history.pushState(null, '', '/')
  }

  onMount(() => {
    notifyNative("userDidLogin")

    if (native) {
      window.loopsOpenSearch = () => { searchOpen = true }
    }

    loadNeuronFromUrl()

    async function handlePopState(event) {
      if (event.state?.neuronId) {
        currentNeuronId = event.state.neuronId
        currentNeuronTitle = event.state.neuronTitle
        currentNeuronBody = event.state.neuronBody || null
        ancestors = event.state.ancestors || []
        resetCollapse()
      } else {
        currentNeuronId = null
        currentNeuronTitle = null
        currentNeuronBody = null
        ancestors = []
      }
    }

    window.addEventListener('popstate', handlePopState)

    function handleGlobalKeydown(event) {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        searchOpen = true
      }
    }

    window.addEventListener('keydown', handleGlobalKeydown)

    const observer = new ResizeObserver(() => resetCollapse())
    if (navEl) observer.observe(navEl)

    return () => {
      window.removeEventListener('popstate', handlePopState)
      window.removeEventListener('keydown', handleGlobalKeydown)
      observer.disconnect()
    }
  })

  async function handleLogout() {
    notifyNative("userDidLogout")
    await fetch('/api/sessions', {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken() },
    })

    window.location.reload()
  }
</script>

<div class="min-h-screen bg-ground text-text">
  {#if !native}
    <header class="border-b border-border px-4 sm:px-6 py-4 flex items-center justify-between">
      <nav bind:this={navEl} data-testid="breadcrumb" class="flex items-center gap-2 min-w-0 overflow-hidden whitespace-nowrap">
        {#if currentNeuronId}
          <button
            onclick={navigateHome}
            class="font-display text-xl text-synapse hover:text-synapse-glow transition-colors cursor-pointer min-h-[44px] flex items-center"
          >
            Loops
          </button>
          {#each displayItems as item}
            <span class="text-text-muted">/</span>
            {#if item.type === 'ellipsis'}
              <span class="text-text-muted">...</span>
            {:else}
              <button
                onclick={() => navigateTo(item)}
                class="text-node hover:text-node-bright transition-colors cursor-pointer font-display text-xl min-h-[44px] flex items-center"
              >
                {item.title}
              </button>
            {/if}
          {/each}
        {:else}
          <h1 class="font-display text-xl text-synapse">Loops</h1>
        {/if}
      </nav>
      <div class="flex items-center gap-4 shrink-0 ml-4">
        <button
          data-testid="search-button"
          onclick={() => searchOpen = true}
          class="flex items-center justify-center min-w-[44px] min-h-[44px] text-text-muted opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
          aria-label="Search neurons"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="9" r="6"/>
            <path d="M13.5 13.5L17 17"/>
          </svg>
        </button>
        <span class="hidden sm:inline text-sm text-text-muted">{email}</span>
        <button
          onclick={handleLogout}
          class="hidden sm:inline text-sm text-node hover:text-node-bright transition-colors"
        >
          Sign out
        </button>
      </div>
    </header>
  {/if}

  <main class="p-4 sm:p-6 max-w-2xl mx-auto">
    {#if currentNeuronTitle}
      <div class="relative mb-6">
        {#if titleSaved}
          <svg class="absolute -left-7 top-3 text-node" width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 8.5l3.5 3.5 6.5-7"/>
          </svg>
        {/if}
        <NeuronEditor
          title={currentNeuronTitle}
          body={currentNeuronBody}
          onSave={saveCurrentNeuron}
          onSaved={showTitleSaved}
          testId="current-neuron"
        />
      </div>
    {/if}
    {#if initialized}
      <NeuronList parentId={currentNeuronId} onNavigate={navigateTo} />
    {/if}
  </main>
</div>

<SearchPalette
  open={searchOpen}
  onClose={() => searchOpen = false}
  onNavigate={(neuron) => { searchOpen = false; navigateTo(neuron); }}
/>
